import {
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { UntypedFormControl } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { takeUntil, distinctUntilChanged, filter, map, startWith } from "rxjs/operators";

import { NoteTarget, ActivityEvent, NoteDetails } from "generated/graphql";
import { NoteDialogComponent } from "app/note/dialogs/note-dialog/note-dialog.component";
import { CampaignFileDialog } from "campaign/dialogs/campaign-file-dialog/campaign-file.dialog";
import { WithDisposableSubject } from "core/mixins/with-disposable-subject";
import { RuleSet } from "query-builder/query.interfaces";
import { QueryStorageService } from "query-builder/core/query-storage.service";
import { CampaignDashboardService } from "campaign/services/campaign-dashboard.service";
import { DefaultRulesetService } from 'query-builder/core/get-default-ruleset.service';
import { Observable } from "rxjs";

const LOADING_MSG = "Loading...";

@Component({
  selector: "app-campaign-home-page",
  templateUrl: "./campaign-home.page.html",
  styleUrls: ["./campaign-home.page.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class CampaignHomePage extends WithDisposableSubject() implements OnInit {
  campaignId: string;
  tabIndex = 0;
  notes = [];
  filteredNotes$: Observable<NoteDetails.Fragment[]>;

  quickSearchInput = new UntypedFormControl();
  loadingMsg = LOADING_MSG;

  totalEntitiesCount: string = LOADING_MSG;
  flaggedCount: string = LOADING_MSG;
  flaggedForCurrentUserCount: string = LOADING_MSG;
  flaggedByUserCount: string = LOADING_MSG;
  activities: ActivityEvent[];
  assignedTasksText: string = LOADING_MSG;
  nextTaskDueText: string = LOADING_MSG;
  totalTasksText: string = LOADING_MSG;

  allEntityTypesQueryParams: any;
  flaggedEntitiesQueryParams: any;
  flaggedForCurrentUserQueryParams: any;
  flaggedByYouQueryParams: any;

  constructor(
    private _dialog: MatDialog,
    private _route: ActivatedRoute,
    private _queryStorageService: QueryStorageService,
    private _dashboardService: CampaignDashboardService,
    private _cd: ChangeDetectorRef,
    private _defaultRulesetService: DefaultRulesetService
  ) {
    super();
  }

  ngOnInit() {
    // Subscribe to query parameter changes to react to campaignId changes
    this._route.queryParams
      .pipe(
        map(params => params.campaignId),
        distinctUntilChanged(),
        filter(campaignId => !!campaignId),
        takeUntil(this.disposer)
      )
      .subscribe((campaignId) => {
        this.campaignId = campaignId;
        this.resetLoadingStates();
        this.setupQueryParams();
        this.loadDashboard();
      });
  }

  private resetLoadingStates() {
    this.totalEntitiesCount = LOADING_MSG;
    this.flaggedCount = LOADING_MSG;
    this.flaggedForCurrentUserCount = LOADING_MSG;
    this.flaggedByUserCount = LOADING_MSG;
    this.assignedTasksText = LOADING_MSG;
    this.nextTaskDueText = LOADING_MSG;
    this.totalTasksText = LOADING_MSG;
    this.notes = [];
    this.activities = null;
  }

  private setupQueryParams() {
    const allEntityTypesRuleSet: RuleSet = {
      condition: "and",
      rules: [{ field: "entity_type", operator: "in", value: [] }],
    };
    this.allEntityTypesQueryParams = {
      campaignId: this.campaignId,
      clientQueryId: this._queryStorageService.saveQuery(this.campaignId, allEntityTypesRuleSet).clientQueryId,
    };

    // Flagged by anyone
    this._defaultRulesetService.getFlaggedEntitiesRuleset().subscribe(ruleSet => {
      this.flaggedEntitiesQueryParams = {
        campaignId: this.campaignId,
        clientQueryId: this._queryStorageService.saveQuery(this.campaignId, ruleSet).clientQueryId,
      };
    });

    // Flagged for current user
    this._defaultRulesetService.getFollowUpRuleset().subscribe(ruleSet => {
      this.flaggedForCurrentUserQueryParams = {
        campaignId: this.campaignId,
        clientQueryId: this._queryStorageService.saveQuery(this.campaignId, ruleSet).clientQueryId,
      };
    });

    // Flagged by current user
    this._defaultRulesetService.getFlaggedByYouRuleset().subscribe(ruleSet => {
      this.flaggedByYouQueryParams = {
        campaignId: this.campaignId,
        clientQueryId: this._queryStorageService.saveQuery(this.campaignId, ruleSet).clientQueryId,
      };
    });
  }

  private loadDashboard() {
    this._dashboardService
      .getDashboardData(this.campaignId)
      .pipe(takeUntil(this.disposer))
      .subscribe((data) => {
        this.totalEntitiesCount = `${data.totalEntitiesCount} ${data.totalEntitiesCount === 1 ? "entity" : "entities"}`;
        this.notes = data.notes;

        // TODO: Someday we should move the quick search within the app-view-notes component itself instead of doing it here in the page component, but for now this is a quick way to get it working without having to refactor the view notes component
        // it would let us controller the add note behavior better to reset or apply the existing filter.
        this.filteredNotes$ = this.quickSearchInput.valueChanges.pipe(
          startWith(''),
          map(val => this.notes.filter(n => `${n.text} ${n.createdBy.name}`.toLowerCase().includes(val.toLowerCase())))
        );
        this.flaggedCount = `${data.flaggedCount} flagged`;
        this.flaggedForCurrentUserCount = `${data.flaggedForCurrentUserCount} flagged for you`;
        this.flaggedByUserCount = `${data.flaggedByUserCount} flagged by you`;
        this.activities = data.activities;
        this.assignedTasksText = `You have ${data.assignedTasksCount} assigned tasks`;
        this.totalTasksText = `Manage ${data.totalTasksCount} tasks`;
        this.nextTaskDueText = data.nextTaskDueText;
        this._cd.markForCheck();
      });
  }

  onAddNoteClick() {
    NoteDialogComponent.open(this._dialog, {
      data: {
        campaignId: this.campaignId,
        targetId: this.campaignId,
        targetType: NoteTarget.Campaign,
        noteType: "text",
        heading: "Add new note",
        hideNotes: true,
      },
    });
  }

  onAddFileClick() {
    CampaignFileDialog.open(this._dialog, {
      data: { campaignId: this.campaignId },
    });
  }
}
